import * as focusTrap from 'focus-trap'
import { addScrollEvent } from './scroll-event'

const mc = {
    menu: 'menu',
    animating: 'menu--animating',
    toggle: 'menu__toggle',
    nav: 'menu__nav',
    item: 'menu__item',
    subMenu: 'menu__sub-menu',
    subMenuToggle: 'menu__sub-menu-toggle',
    itemHasChildren: 'menu-item-has-children',
}

type NavBarOpts = {
    listenForScroll?: boolean
    breakpoint?: number | false
}

export class NavBar {
    container: HTMLElement
    toggler: HTMLButtonElement
    bar: HTMLElement
    hasChildren: HTMLElement[]
    navMenuLinks: NodeListOf<Element>
    breakpoint: number | false
    mobile: boolean
    trap: focusTrap.FocusTrap

    constructor(
        wrapper: HTMLElement,
        { listenForScroll = false, breakpoint = 768 }: NavBarOpts = {}
    ) {
        this.container = wrapper

        let toggler = this.container.querySelector(`.${mc.toggle}`)
        if (!(toggler instanceof HTMLButtonElement)) throw new Error('No menu toggle found')
        this.toggler = toggler

        let bar = this.container.querySelector(`.${mc.nav}`)
        if (!(bar instanceof HTMLElement)) throw new Error('No menu bar found')
        this.bar = bar

        let hasChildrenEls = this.container.querySelectorAll(`.${mc.itemHasChildren}`)
        this.hasChildren = Array.from(hasChildrenEls) as HTMLElement[]

        this.navMenuLinks = this.bar.querySelectorAll('a')
        this.breakpoint = breakpoint
        this.mobile = this.breakpoint ? window.innerWidth < this.breakpoint : true

        if (listenForScroll) this.scrollEvent()

        this.trap = focusTrap.createFocusTrap(this.container, {
            clickOutsideDeactivates: false,
            escapeDeactivates: false,
        })

        this.init()
    }

    get isActive() {
        return this.trap.active
    }

    get isAnimating() {
        return this.container.classList.contains(mc.animating)
    }

    init() {
        this.mobile ? this.setMobile() : this.setDesktop()
        this.toggler.addEventListener('click', this.toggle)
        this.container.addEventListener('keydown', this.keypress)

        if (this.breakpoint) this.monitorSize()
    }

    scrollEvent() {
        addScrollEvent({
            position: 300,
            onEnter: () => {
                this.container.classList.add('scrolled')
            },
            onLeave: () => {
                this.container.classList.remove('scrolled')
            },
        })
    }

    keypress = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            if (this.isActive && !this.isAnimating) this.closeMenu()
        }
    }

    setMobile() {
        this.bar.setAttribute('aria-hidden', 'true')
        this.toggler.setAttribute('aria-expanded', 'false')
        this.container.classList.remove('menu--desktop')
        this.bar.style.display = 'none'

        this.hasChildren?.forEach((item) => {
            let subMenu = item.querySelector(`.${mc.subMenu}`)
            let subMenuToggle = item.querySelector(`.${mc.subMenuToggle}`)

            subMenu && subMenu.setAttribute('aria-hidden', 'false')
            item.removeEventListener('mouseenter', this.dropdownMouseEnter)
            item.removeEventListener('mouseout', this.dropdownMouseOut)
            item.removeEventListener('focusin', this.dropdownFocusIn)
            item.removeEventListener('focusout', this.dropdownFocusOut)
        })
    }

    setDesktop() {
        this.trap.deactivate()
        this.bar.setAttribute('aria-hidden', 'false')
        this.container.classList.add('menu--desktop')
        document.documentElement.style['overflow'] = 'auto'

        this.bar.style.transform = 'none'
        this.bar.style.display = 'block'

        this.hasChildren?.forEach((item) => {
            let subMenu = item.querySelector(`.${mc.subMenu}`)
            let toggler = item.querySelector(`.${mc.subMenuToggle}`)

            subMenu?.setAttribute('aria-hidden', 'true')
            toggler?.setAttribute('aria-expanded', 'false')
            item.addEventListener('mouseenter', this.dropdownMouseEnter)
            item.addEventListener('mouseleave', this.dropdownMouseOut)
            item.addEventListener('focusout', this.dropdownFocusOut)
            item.addEventListener('focusin', this.dropdownFocusIn)
        })
    }

    monitorSize() {
        window.addEventListener('resize', () => {
            if (!this.breakpoint) return
            let w = window.innerWidth

            if (!this.mobile && w < this.breakpoint) {
                this.setMobile()
                this.mobile = true
            } else if (this.mobile && w >= this.breakpoint) {
                this.mobile = false
                this.setDesktop()
            }
        })
    }

    toggle = () => {
        if (this.isAnimating) return
        this.isActive ? this.closeMenu() : this.openMenu()
    }

    openMenu() {
        this.container.classList.add(mc.animating)

        document.documentElement.style['overflow'] = 'hidden'
        this.toggler.setAttribute('aria-expanded', 'true')
        this.bar.setAttribute('aria-hidden', 'false')
        this.bar.style.display = 'block'

        let animation = this.bar.animate(
            [
                { transform: 'translateY(-100%)', opacity: '0' },
                { transform: 'none', opacity: '1' },
            ],
            { duration: 300 }
        )
        animation.onfinish = () => {
            this.container.classList.remove(mc.animating)
            animation.commitStyles()
            this.trap.activate()
        }
    }

    closeMenu() {
        this.container.classList.add(mc.animating)
        document.documentElement.style['overflow'] = 'auto'
        this.toggler.setAttribute('aria-expanded', 'false')
        this.bar.setAttribute('aria-hidden', 'true')

        let animation = this.bar.animate(
            [
                { transform: 'none', opacity: '1' },
                { transform: 'translateY(-100%)', opacity: '0' },
            ],
            { duration: 300 }
        )

        animation.onfinish = () => {
            this.trap.deactivate()
            this.bar.style.display = 'none'
            this.container.classList.remove(mc.animating)
        }
    }

    dropdownOpen = (item: HTMLElement) => {
        let subMenuToggle = item.querySelector(`.${mc.subMenuToggle}`)
        let subMenu = item.querySelector(`.${mc.subMenu}`)
        subMenu?.setAttribute('aria-hidden', 'false')
        subMenuToggle?.setAttribute('aria-expanded', 'true')
    }

    dropdownClose = (item: HTMLElement) => {
        let subMenuToggle = item.querySelector(`.${mc.subMenuToggle}`)
        let subMenu = item.querySelector(`.${mc.subMenu}`)
        subMenu?.setAttribute('aria-hidden', 'true')
        subMenuToggle?.setAttribute('aria-expanded', 'false')
    }

    dropdownFocusIn = (e: FocusEvent) => {
        let item = e.currentTarget as HTMLElement
        if (this.dropdownIsActive(item)) return

        this.dropdownOpen(item)
    }

    dropdownMouseOut = (e: MouseEvent) => {
        let item = e.target
        if (item instanceof HTMLElement) {
            let dropdown = item.closest(`.${mc.item}`)
            dropdown instanceof HTMLElement && this.dropdownClose(dropdown)
        }
    }

    dropdownMouseEnter = (e: MouseEvent) => {
        let item = e.currentTarget as HTMLElement
        if (this.dropdownIsActive(item)) return
        this.dropdownOpen(item)
    }

    dropdownFocusOut = (e: FocusEvent) => {
        let item = e.currentTarget as HTMLElement
        let relatedTarget = e.relatedTarget
        if (!(relatedTarget instanceof HTMLElement) || !item.contains(relatedTarget)) {
            this.dropdownClose(item)
        }
    }

    dropdownIsActive = (item: HTMLElement) => {
        let subMenu = item.querySelector(`.${mc.subMenu}`)
        return subMenu?.getAttribute('aria-hidden') === 'false'
    }
}

// let menu = document.querySelector(`.${mc.menu}`) as HTMLElement
// if (menu) {
//     new navBar(menu)
// }
